import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useAuthContext } from '../../../hooks/agencyAuthHook';
import toast from 'react-hot-toast';
import InstructionsHook from '../../../hooks/InstructionsHook';
import { franc } from 'franc-min';

const ToolbarButton = ({ title, isActive, onMouseDown, icon }) => (
  <button
    type="button"
    title={title}
    onMouseDown={onMouseDown}
    className={`toolbar-button ${isActive ? 'active' : ''}`}
  >
    {icon}
  </button>
);

export default function Instructions() {
  const { getInstructions } = InstructionsHook();
  const { agency } = useAuthContext();
  const apiUrl = process.env.REACT_APP_API_URL;
  const instructions = useSelector((state) => state.instructions.instructions);
  
  const editorRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeStyles, setActiveStyles] = useState({});
  const [currentFont, setCurrentFont] = useState('Arial');
  const [currentFontSize, setCurrentFontSize] = useState('3');
  const [currentColor, setCurrentColor] = useState('#000000');
  const [currentLineHeight, setCurrentLineHeight] = useState('1.5');
  const [currentCharacterSpacing, setCurrentCharacterSpacing] = useState('normal');
  const [currentWordSpacing, setCurrentWordSpacing] = useState('normal');
  const [isUrdu, setIsUrdu] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [fontsLoaded, setFontsLoaded] = useState(false);

  // Preload Urdu font
  useEffect(() => {
    const loadFonts = async () => {
      try {
        const font = new FontFace(
          'Noto Nastaliq Urdu',
          'url(https://fonts.gstatic.com/s/notonastaliqurdu/v23/LhW9MV1gA5luiqz6_DCA5C6M6BqKZthnkv6EhZVeBNWQ.woff2)'
        );
        await font.load();
        document.fonts.add(font);
        setFontsLoaded(true);
      } catch (error) {
        console.error('Failed to load Urdu font:', error);
        setFontsLoaded(true); // Continue even if font fails to load
      }
    };

    loadFonts();
  }, []);

  useEffect(() => {
    document.title = 'Users Instructions';
    getInstructions();
  }, []);

  useEffect(() => {
    if (editorRef.current && instructions?.content) {
      if (editorRef.current.innerHTML !== instructions.content) {
        editorRef.current.innerHTML = instructions.content;
      }
      setIsUrdu(instructions.isUrdu);
      
      setTimeout(() => {
        updateToolbar();
      }, 0);
    }
  }, [instructions]);

  const applyBlockClass = (prefix, value) => {
    if (!editorRef.current) return;
    
    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    
    editorRef.current.focus();
    document.execCommand('formatBlock', false, 'p');

    selection.removeAllRanges();
    selection.addRange(range);

    let element = range.commonAncestorContainer;
    while (element && element !== editorRef.current) {
      if (element.nodeType === 1 && window.getComputedStyle(element).display.includes('block')) {
        const classes = element.className.split(' ').filter(c => !c.startsWith(prefix));
        if (value !== 'normal' && value !== '1.5') {
          classes.push(`${prefix}-${value.replace('.', '-')}`);
        }
        element.className = classes.join(' ');
        break;
      }
      element = element.parentNode;
    }
    updateToolbar();
  };
  
  const execCmd = useCallback((command, value = null) => {
    if (!editorRef.current) return;
    
    const selection = window.getSelection();
    const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
    
    document.execCommand(command, false, value);
    editorRef.current.focus();
    
    if (range) {
      selection.removeAllRanges();
      selection.addRange(range);
    }
    
    updateToolbar();
  }, []);

  const applyFontFamily = useCallback((font) => {
    if (!editorRef.current) return;
  
    try {
      // First try the standard execCommand approach
      document.execCommand('styleWithCSS', false, true);
      document.execCommand('fontName', false, font);
      
      // Fallback for browsers that don't support fontName properly
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        
        // Skip if range is collapsed (no selection)
        if (range.collapsed) {
          // Apply to parent element if no text is selected
          const parent = range.startContainer.parentElement;
          parent.style.fontFamily = font;
          return;
        }
  
        // Check if the range can be surrounded
        try {
          const span = document.createElement('span');
          span.style.fontFamily = font;
          
          // Clone the range to test if we can surround contents
          const testRange = range.cloneRange();
          testRange.surroundContents(document.createElement('span'));
          testRange.detach(); // Clean up test range
          
          // If we get here, we can safely surround
          range.surroundContents(span);
        } catch (e) {
          // If surrounding fails, apply to each text node in the range
          const walker = document.createTreeWalker(
            range.commonAncestorContainer,
            NodeFilter.SHOW_TEXT,
            {
              acceptNode: (node) => {
                return range.intersectsNode(node) 
                  ? NodeFilter.FILTER_ACCEPT 
                  : NodeFilter.FILTER_REJECT;
              }
            }
          );
  
          const textNodes = [];
          while (walker.nextNode()) textNodes.push(walker.currentNode);
  
          textNodes.forEach(node => {
            const span = document.createElement('span');
            span.style.fontFamily = font;
            node.parentNode.insertBefore(span, node);
            span.appendChild(node);
          });
        }
      }
      
      setCurrentFont(font);
      updateToolbar();
    } catch (error) {
      console.error('Error applying font:', error);
      // Fallback to simple execCommand if all else fails
      document.execCommand('fontName', false, font);
    }
  }, []);

  const handleCreateLink = useCallback(() => {
    const url = prompt("Enter the URL:");
    if (url) {
      execCmd('createLink', url);
    }
  }, [execCmd]);

  const updateToolbar = useCallback(() => {
    if (!editorRef.current) return;
    
    const textContent = editorRef.current.textContent || '';
    const detectedLanguage = franc(textContent);
    setIsUrdu(detectedLanguage === "urd");
    
    if (editorRef.current) {
      editorRef.current.style.direction = isUrdu ? 'rtl' : 'ltr';
      editorRef.current.style.textAlign = isUrdu ? 'right' : 'left';
    }

    const styles = {};
    const commands = ['bold', 'italic', 'underline', 'strikethrough', 'justifyLeft', 'justifyCenter', 'justifyRight', 'insertOrderedList', 'insertUnorderedList'];
    commands.forEach(command => {
      styles[command] = document.queryCommandState(command);
    });
    
    setActiveStyles(styles);
    
    // Improved font detection
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const node = range.startContainer;
      const element = node.nodeType === 3 ? node.parentElement : node;
      const computedStyle = window.getComputedStyle(element);
      
      setCurrentFont(computedStyle.fontFamily.replace(/['"]/g, ''));
      setCurrentFontSize(document.queryCommandValue('fontSize'));
      setCurrentColor(rgbToHex(document.queryCommandValue('foreColor')));
    }

    const selectionForSpacing = window.getSelection();
    if (!selectionForSpacing.rangeCount) return;
    
    let element = selectionForSpacing.getRangeAt(0).commonAncestorContainer;
    let lh = '1.5', cs = 'normal', ws = 'normal';

    while (element && element !== editorRef.current) {
      if (element.nodeType === 1) {
        element.classList.forEach(cls => {
          if (cls.startsWith('line-height-')) {
            lh = cls.replace('line-height-', '').replace('-', '.');
          }
          if (cls.startsWith('char-spacing-')) {
            cs = cls.replace('char-spacing-', '');
          }
          if (cls.startsWith('word-spacing-')) {
            ws = cls.replace('word-spacing-', '');
          }
        });
        if (window.getComputedStyle(element).display.includes('block')) break;
      }
      element = element.parentNode;
    }
    setCurrentLineHeight(lh);
    setCurrentCharacterSpacing(cs);
    setCurrentWordSpacing(ws);
  }, [isUrdu]);
  
  const rgbToHex = (rgb) => {
    if (!rgb || rgb.startsWith('#')) return rgb || '#000000';
    let sep = rgb.indexOf(",") > -1 ? "," : " ";
    rgb = rgb.substr(4).split(")")[0].split(sep);
    let r = (+rgb[0]).toString(16),
        g = (+rgb[1]).toString(16),
        b = (+rgb[2]).toString(16);
    if (r.length === 1) r = "0" + r;
    if (g.length === 1) g = "0" + g;
    if (b.length === 1) b = "0" + b;
    return "#" + r + g + b;
  }

  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Noto+Nastaliq+Urdu:wght@400;700&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);

    const editorNode = editorRef.current;
    if (!editorNode) return;

    const handleInput = () => updateToolbar();
    
    if (!isInitialized) {
      editorNode.innerHTML = instructions?.content || 'Type your Instructions in Urdu or English. It will appear on Voucher...';
      setIsInitialized(true);
    }

    editorNode.addEventListener('input', handleInput);
    document.addEventListener('selectionchange', updateToolbar);

    return () => {
      document.head.removeChild(link);
      editorNode.removeEventListener('input', handleInput);
      document.removeEventListener('selectionchange', updateToolbar);
    };
  }, [instructions, isInitialized, updateToolbar]);

  const addInstructions = async () => {
    if (!editorRef.current) return;
    
    setIsLoading(true);
    let cleanedContent = editorRef.current.innerHTML
      .replace(/<p>\s*<\/p>/g, "")
      .replace(/\s+/g, " ")
      .trim();
    
    toast.promise(
      new Promise(async (resolve, reject) => {
        try {
          const response = await fetch(`${apiUrl}/api/agencies/instructions/add`, {
            method: "POST",
            headers: {
              'Authorization': `Bearer ${agency.token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ content: cleanedContent, isUrdu }),
          });
          const json = await response.json();

          if (response.ok) {
            resolve(json.message);
            getInstructions();
          } else {
            reject(new Error(json.message || "Failed to save."));
          }
        } catch (error) {
          reject(new Error(error.message || "Unexpected error occurred."));
        } finally {
          setIsLoading(false);
        }
      }),
      {
        loading: "Saving...",
        success: (message) => <b>{message || "Saved successfully!"}</b>,
        error: (error) => <b>{error.message || "Failed to save."}</b>,
      }
    );
  }

  const handleCommand = (e, cmd, val = null) => {
    e.preventDefault();
    if (cmd === 'fontName') {
      applyFontFamily(val);
    } else {
      execCmd(cmd, val);
    }
  };

  const icons = {
    bold: <b>B</b>,
    italic: <i>I</i>,
    underline: <u>U</u>,
    strikethrough: <span style={{textDecoration: 'line-through'}}>S</span>,
    justifyLeft: <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M3 4h18v2H3zm0 5h12v2H3zm0 5h18v2H3zm0 5h12v2H3z"/></svg>,
    justifyCenter: <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M3 4h18v2H3zm3 5h12v2H6zm-3 5h18v2H3zm3 5h12v2H6z"/></svg>,
    justifyRight: <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M3 4h18v2H3zm6 5h12v2H9zm-6 5h18v2H3zm6 5h12v2H9z"/></svg>,
    insertOrderedList: <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M4 4h2v2H4zm0 5h2v2H4zm0 5h2v2H4zM8 5v1h12V5H8zm0 6v1h12v-1H8zm0 6v1h12v-1H8z"/></svg>,
    insertUnorderedList: <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M4 4h2v2H4zm0 5h2v2H4zm0 5h2v2H4zM8 5v2h12V5H8zm0 6v2h12v-2H8zm0 6v2h12v-2H8z"/></svg>,
    undo: <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12.5 8c-2.65 0-5.05.99-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C20.36 11.23 16.72 8 12.5 8z"/></svg>,
    redo: <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M18.4 10.6C16.55 8.99 14.15 8 11.5 8c-4.22 0-7.86 3.23-9.13 7.5l-2.37-.78C1.45 10.31 5.94 6 11.5 6c2.66 0 5.06 1.01 6.9 2.6L22 5v9h-9l3.4-3.4z"/></svg>,
    createLink: <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/></svg>,
    removeFormat: <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12.87 15.43l-2.83-2.83L12.87 9.77l1.41 1.41-1.41 1.42 1.41 1.42-1.41 1.41zm-4.24-4.24L6.77 9.33 5.36 7.92 3.95 9.33 5.36 10.74l1.41-1.41zm5.3 5.3l1.41-1.41 1.42 1.41-1.41 1.41-1.42-1.41zM6.2 21h11.6c.66 0 1.2-.54 1.2-1.2V7.8c0-.66-.54-1.2-1.2-1.2H6.2c-.66 0-1.2.54-1.2 1.2v12c0 .66.54 1.2 1.2 1.2zM8.5 5.4H15.5V4H8.5v1.4z"/></svg>,
  };

  return (
    <div className="instructions-container px-0 mx-0">
      <div className="instructions-wrapper px-0">
        <div className="instructions-card">
          <div className="instructions-header">
            <h4>
              Instructions <span>(At Vouchers)</span>
            </h4>
            <button
              type="button"
              onClick={addInstructions}
              className="save-button"
              disabled={isLoading}
            >
              {isLoading ? "Saving" : "Save"}
            </button>
          </div>
          
          <div className="editor-container">
            <div className="toolbar">
              <ToolbarButton title="Undo" onMouseDown={(e) => handleCommand(e, 'undo')} icon={icons.undo} />
              <ToolbarButton title="Redo" onMouseDown={(e) => handleCommand(e, 'redo')} icon={icons.redo} />
              <div className="toolbar-divider"></div>
              
              <ToolbarButton title="Bold" isActive={activeStyles.bold} onMouseDown={(e) => handleCommand(e, 'bold')} icon={icons.bold} />
              <ToolbarButton title="Italic" isActive={activeStyles.italic} onMouseDown={(e) => handleCommand(e, 'italic')} icon={icons.italic} />
              <ToolbarButton title="Underline" isActive={activeStyles.underline} onMouseDown={(e) => handleCommand(e, 'underline')} icon={icons.underline} />
              <ToolbarButton title="Strikethrough" isActive={activeStyles.strikethrough} onMouseDown={(e) => handleCommand(e, 'strikethrough')} icon={icons.strikethrough} />
              <div className="toolbar-divider"></div>
              
              <select 
                value={currentLineHeight} 
                title="Line Spacing" 
                onChange={(e) => applyBlockClass('line-height', e.target.value)} 
                className="toolbar-select"
              >
                <option value="1">Single</option>
                <option value="1.5">1.5</option>
                <option value="2">Double</option>
              </select>
              
              <select 
                value={currentCharacterSpacing} 
                title="Character Spacing" 
                onChange={(e) => applyBlockClass('char-spacing', e.target.value)} 
                className="toolbar-select"
              >
                <option value="normal">Normal</option>
                <option value="expanded">Expanded</option>
                <option value="condensed">Condensed</option>
              </select>
              
              <select 
                value={currentWordSpacing} 
                title="Word Spacing" 
                onChange={(e) => applyBlockClass('word-spacing', e.target.value)} 
                className="toolbar-select"
              >
                <option value="normal">Normal</option>
                <option value="wide">Wide</option>
                <option value="wider">Wider</option>
              </select>
              
              <div className="toolbar-divider"></div>
              
              <ToolbarButton title="Align Left" isActive={activeStyles.justifyLeft} onMouseDown={(e) => handleCommand(e, 'justifyLeft')} icon={icons.justifyLeft} />
              <ToolbarButton title="Align Center" isActive={activeStyles.justifyCenter} onMouseDown={(e) => handleCommand(e, 'justifyCenter')} icon={icons.justifyCenter} />
              <ToolbarButton title="Align Right" isActive={activeStyles.justifyRight} onMouseDown={(e) => handleCommand(e, 'justifyRight')} icon={icons.justifyRight} />
              <div className="toolbar-divider"></div>
              
              <ToolbarButton title="Ordered List" isActive={activeStyles.insertOrderedList} onMouseDown={(e) => handleCommand(e, 'insertOrderedList')} icon={icons.insertOrderedList} />
              <ToolbarButton title="Unordered List" isActive={activeStyles.insertUnorderedList} onMouseDown={(e) => handleCommand(e, 'insertUnorderedList')} icon={icons.insertUnorderedList} />
              <ToolbarButton title="Add Link" onMouseDown={(e) => { e.preventDefault(); handleCreateLink(); }} icon={icons.createLink} />
              <div className="toolbar-divider"></div>
              
              <select 
                value={currentFont} 
                onChange={(e) => handleCommand(e, 'fontName', e.target.value)} 
                className="toolbar-select"
                style={{ fontFamily: currentFont }}
              >
                <option value="Arial" style={{fontFamily: 'Arial'}}>Arial</option>
                <option value="Verdana" style={{fontFamily: 'Verdana'}}>Verdana</option>
                <option value="Times New Roman" style={{fontFamily: 'Times New Roman'}}>Times New Roman</option>
                <option value="Noto Nastaliq Urdu" style={{fontFamily: "'Noto Nastaliq Urdu', serif"}}>
                  Noto Nastaliq Urdu
                </option>
                <option value="Jameel Noori Nastaleeq" style={{fontFamily: "'Jameel Noori Nastaleeq', serif"}}>
                  Jameel Noori Nastaleeq
                </option>
              </select>
              
              <select 
                value={currentFontSize} 
                onChange={(e) => execCmd('fontSize', e.target.value)} 
                className="toolbar-select"
              >
                <option value="1">8px</option>
                <option value="2">10px</option>
                <option value="3">12px</option>
                <option value="4">14px</option>
                <option value="5">18px</option>
                <option value="6">24px</option>
                <option value="7">36px</option>
              </select>
              
              <input 
                type="color" 
                value={currentColor} 
                onChange={(e) => execCmd('foreColor', e.target.value)} 
                className="color-picker" 
                title="Text Color" 
              />
              
              <ToolbarButton title="Clear Formatting" onMouseDown={(e) => handleCommand(e, 'removeFormat')} icon={icons.removeFormat} />
            </div>

            <div
              ref={editorRef}
              contentEditable="true"
              className="editor-area"
              dir={isUrdu ? 'rtl' : 'ltr'}
              style={{ 
                textAlign: isUrdu ? 'right' : 'left',
                fontFamily: isUrdu ? "'Noto Nastaliq Urdu', serif" : currentFont
              }}
              suppressContentEditableWarning
            />
          </div>
        </div>
      </div>
    </div>
  );
}